import cx from 'classnames';
import { map, split } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QrCode } from 'components';
import { BANK_STYLES } from 'constants/bank-styles';
import { IMAGES_PATH } from 'constants/images.constants';
import { DeviceOS, Bank } from 'enums';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Deeplink } from 'types';
import { userDeviceUtils } from 'utils';

type Props = {
  deeplinks: Deeplink[];
};

export const Deeplinks: React.FC<Props> = ({ deeplinks }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const { order, getBankNameByCode } = useOrderContext();
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  const processDeeplinkUrl = useCallback(
    (url: string) =>
      url
        .replaceAll('{{phone}}', order?.requisites?.phone?.replace(/\D/g, '')!)
        .replaceAll(
          '{{card}}',
          order?.requisites?.cardInfo?.replace(/\D/g, '')!,
        )
        .replaceAll('{{amount}}', String(order?.amount || 0))
        .replaceAll(
          '{{payment_string}}',
          order?.requisites?.paymentString ?? '',
        )
        .replaceAll(
          '{{payment_string_vietnam_sber_qr}}',
          order?.requisites?.paymentStringVietnamSberQR ?? '',
        ),
    [order],
  );

  return (
    <div className="tw-mb-4">
      {map(deeplinks, (deeplink) => {
        if (!deeplink.url) {
          return;
        }
        const url = processDeeplinkUrl(deeplink.url);
        const hasMultipleUrls = url.includes(',');
        return (
          <div key={deeplink.id} className="tw-max-w-sm tw-m-auto">
            {deviceOS === DeviceOS.Desktop && !hasMultipleUrls && (
              <Fragment>
                <QrCode
                  data={url}
                  title={t('qr_code.title', {
                    bankName:
                      getBankNameByCode?.(deeplink.customerBank) ||
                      deeplink.customerBank,
                  })}
                />
                <div className="tw-text-gray-400 tw-text-center tw-w-full tw-mb-4 tw-text-sm">
                  {t('qr_code.description')}
                </div>
              </Fragment>
            )}
            <DeepLinkButton href={url} bank={deeplink.customerBank as Bank} />
          </div>
        );
      })}
    </div>
  );
};

function DeepLinkButton({ href, bank }: { href: string; bank: Bank }) {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { getBankNameByCode } = useOrderContext();

  const [isLoading, setIsLoading] = useState(false);

  const style = useMemo(() => BANK_STYLES[bank], [bank]);

  const title = useMemo(
    () =>
      t('deeplink.title', {
        bankName: style?.name || getBankNameByCode?.(bank) || bank,
      }),
    [style?.name, bank, getBankNameByCode, t],
  );

  const content = useMemo(
    () =>
      style?.buttonIconPath ? (
        <img
          src={`${IMAGES_PATH}${style.buttonIconPath}`}
          alt={title}
          width={style.buttonIconWidth}
          height={style.buttonIconHeight}
        />
      ) : (
        <span>{title}</span>
      ),
    [style, title],
  );

  const tryOpenDeepLink = useCallback(
    (url: string, timeoutMs: number = 300): Promise<boolean> =>
      new Promise((resolve) => {
        let resolved = false;

        const cleanup = () => {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          window.removeEventListener('pagehide', handleSuccess);
        };

        const handleSuccess = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve(true);
          }
        };

        const onVisibilityChange = () => {
          if (document.visibilityState === 'hidden') handleSuccess();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', handleSuccess);

        window.location.href = url;

        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(false);
          }
        }, timeoutMs);
      }),
    [],
  );

  const openDeepLinks = useCallback(
    async (href: string) => {
      const urls = split(href, ',');
      for (const url of urls) {
        if (await tryOpenDeepLink(url.trim())) return;
      }
    },
    [tryOpenDeepLink],
  );

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (isLoading) return;

      setIsLoading(true);
      await openDeepLinks(href);
      setIsLoading(false);
    },
    [href, openDeepLinks, isLoading],
  );

  const className = cx(
    `tw-w-full tw-h-[54px] tw-rounded-[8px] tw-text-white tw-font-medium tw-text-[17px] tw-flex tw-items-center tw-justify-center`,
    { 'tw-opacity-50 tw-cursor-wait': isLoading },
  );

  return (
    <a
      className={className}
      style={{ background: style?.gradient }}
      target="_blank"
      rel="noopener noreferrer"
      {...(href.includes(',') ? { onClick } : { href })}
    >
      {content}
    </a>
  );
}
